import { toast } from 'sonner';
import type { ExperienceFormData } from './ExperienceFormFields';
import type { CreateExperienceRequest } from '../types/experience.types';

interface FormModeConfig {
  title: string;
  submitLabel: string;
  loadingLabel: string;
}

export function getFormModeConfig(mode: 'create' | 'edit'): FormModeConfig {
  return mode === 'create'
    ? { title: 'Add Experience', submitLabel: 'Create', loadingLabel: 'Creating...' }
    : { title: 'Edit Experience', submitLabel: 'Save', loadingLabel: 'Saving...' };
}

export const DEFAULT_FORM_VALUES: ExperienceFormData = {
  companyName: '',
  jobTitle: '',
  startYear: '',
  startMonth: '',
  endYear: '',
  endMonth: '',
  isOngoing: false,
  isFullTime: true,
  isContractor: false,
  continuous: true,
  isGig: false,
};

export function toEditValues(exp: {
  companyName: string;
  jobTitle: string;
  isOngoing: boolean;
  isFullTime: boolean;
  isContractor: boolean;
  continuous: boolean;
  isGig: boolean;
  startDate?: { year?: number; month?: number | null } | null;
  endDate?: { year?: number; month?: number | null } | null;
}): ExperienceFormData {
  return {
    companyName: exp.companyName,
    jobTitle: exp.jobTitle,
    startYear: exp.startDate?.year?.toString() ?? '',
    startMonth: exp.startDate?.month?.toString() ?? '',
    endYear: exp.endDate?.year?.toString() ?? '',
    endMonth: exp.endDate?.month?.toString() ?? '',
    isOngoing: exp.isOngoing,
    isFullTime: exp.isFullTime,
    isContractor: exp.isContractor,
    continuous: exp.continuous,
    isGig: exp.isGig,
  };
}

export function buildRequest(data: ExperienceFormData): CreateExperienceRequest {
  return {
    companyName: data.companyName,
    jobTitle: data.jobTitle,
    startDate: data.startYear
      ? {
          year: parseInt(data.startYear),
          month: data.startMonth ? parseInt(data.startMonth) : undefined,
        }
      : undefined,
    endDate:
      !data.isOngoing && data.endYear
        ? {
            year: parseInt(data.endYear),
            month: data.endMonth ? parseInt(data.endMonth) : undefined,
          }
        : undefined,
    isOngoing: data.isOngoing,
    isFullTime: data.isFullTime,
    isContractor: data.isContractor,
    continuous: data.continuous,
    isGig: data.isGig,
  };
}

function hasCompleteDateRange(data: ExperienceFormData): boolean {
  return !data.isOngoing && !!data.startYear && !!data.endYear;
}

export function isEndDateBeforeStart(data: ExperienceFormData): boolean {
  if (!hasCompleteDateRange(data)) return false;
  const startYear = parseInt(data.startYear);
  const endYear = parseInt(data.endYear);
  if (endYear < startYear) return true;
  if (endYear > startYear) return false;
  const startMonth = data.startMonth ? parseInt(data.startMonth) : 0;
  const endMonth = data.endMonth ? parseInt(data.endMonth) : 0;
  // When either month is absent we cannot determine ordering within the same
  // year, so we allow submission. The backend stores year-only precision and
  // ordering is deferred to year-level comparison.
  if (startMonth === 0 || endMonth === 0) return false;
  return endMonth <= startMonth;
}

type MutationCallbacks = { onSuccess: () => void; onError: () => void };

interface SubmitParams {
  candidateId: string;
  request: CreateExperienceRequest;
  onDone: () => void;
}

export function submitCreate(
  createFn: (
    params: { candidateId: string; request: CreateExperienceRequest },
    callbacks: MutationCallbacks
  ) => void,
  { candidateId, request, onDone }: SubmitParams
): void {
  createFn(
    { candidateId, request },
    {
      onSuccess: () => {
        toast.success('Experience created successfully');
        onDone();
      },
      onError: () => toast.error('Failed to create experience'),
    }
  );
}

export function submitUpdate(
  updateFn: (
    params: { candidateId: string; experienceId: string; request: CreateExperienceRequest },
    callbacks: MutationCallbacks
  ) => void,
  experienceId: string,
  { candidateId, request, onDone }: SubmitParams
): void {
  updateFn(
    { candidateId, experienceId, request },
    {
      onSuccess: () => {
        toast.success('Experience updated successfully');
        onDone();
      },
      onError: () => toast.error('Failed to update experience'),
    }
  );
}
